/* jshint ignore:start */
import { useContext, useEffect, useState } from "react";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import { MyContext } from "../../Context/MyProvider";
import { showToast } from "../../showTost";
import V1CashfreeCheckout from "../../Payment/V1CashfreeCheckout";

function today() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function EquipmentRental() {
  const { ApiBaseUrl, setIsLoginShow } = useContext(MyContext);
  const [items, setItems] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [start, setStart] = useState(today());
  const [due, setDue] = useState(addDays(today(), 7));
  const [onlineFlow, setOnlineFlow] = useState(false);

  const loadRentals = () => {
    const uid = secureLocalStorage.getItem("Userid");
    if (!uid) { setRentals([]); return; }
    axios
      .post(`${ApiBaseUrl}/customer/equipment_my_rentals`, { id: uid })
      .then((r) => setRentals(r.data.rentals || []))
      .catch(() => setRentals([]));
  };

  useEffect(() => {
    axios
      .post(`${ApiBaseUrl}/customer/equipment_list`, {})
      .then((r) => setItems(r.data.equipment || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    loadRentals();
    /* eslint-disable-next-line */
  }, []);

  const days = Math.max(1, Math.ceil((new Date(due) - new Date(start)) / 86400000) || 1);
  const estimate = active ? Number(active.daily_rate || 0) * days : 0;
  const payable = estimate + Number((active && active.deposit_amount) || 0);

  const openRent = (item) => {
    const uid = secureLocalStorage.getItem("Userid");
    if (!uid) { setIsLoginShow(true); return; }
    setActive(item);
    setStart(today());
    setDue(addDays(today(), 7));
    setOnlineFlow(false);
  };

  const confirmRent = () => {
    const uid = secureLocalStorage.getItem("Userid");
    if (!uid) { setIsLoginShow(true); return; }
    if (new Date(due) <= new Date(start)) {
      showToast("error", "The return date has to be after the start date.");
      return;
    }
    setOnlineFlow(true);
  };

  const createOnlineCheckout = async () => {
    const uid = secureLocalStorage.getItem("Userid");
    const res = await axios.post(`${ApiBaseUrl}/customer/cashfree-payment`, {
      amount: payable,
      uid,
      status: "1",
    });
    if (!res.data.status || !res.data.payment_session_id) {
      throw new Error(res.data.message || "Online checkout could not start");
    }
    return {
      payment_session_id: res.data.payment_session_id,
      cashfree_order_id: res.data.order_id,
      ref: "equipment",
      environment: res.data.environment,
    };
  };

  const confirmOnline = async ({ cashfree_order_id }) => {
    const uid = secureLocalStorage.getItem("Userid");
    const res = await axios.post(`${ApiBaseUrl}/customer/equipment_rent`, {
      id: uid,
      equipment_id: active.id,
      start_date: start,
      due_date: due,
      payment_method: "online",
      cashfree_order_id,
    });
    if (!res.data.Result) throw new Error(res.data.message || "Confirmation failed");
    return res.data;
  };

  const onlineSuccess = () => {
    showToast("success", "Equipment rental booked");
    setActive(null);
    setOnlineFlow(false);
    loadRentals();
  };

  return (
    <div className="width-container py-[40px]">
      <div className="max-w-[720px]">
        <p className="text-[13px] uppercase tracking-wide font-[700] text-rm-primary">Equipment Rental</p>
        <h1 className="text-[34px] leading-tight font-[800] text-rm-ink mt-[6px]">
          Medical equipment, delivered and collected
        </h1>
        <p className="text-rm-muted mt-[10px]">
          Wheelchairs, oxygen concentrators, hospital beds and monitors on flexible rental. Pay a refundable
          deposit up front and only the daily rate for the days you keep it.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-[14px] mt-[24px]">
        {[
          ["Refundable deposit", "Returned in full when the item comes back on time and undamaged."],
          ["Late returns", "Charged per day as a share of the deposit, and never more than the deposit."],
          ["Condition check", "We run a short checklist at pickup and at return, so there are no surprises."],
        ].map(([title, body]) => (
          <div key={title} className="border border-rm-line rounded-[16px] p-[16px] bg-white">
            <h4 className="font-[700] text-rm-ink">{title}</h4>
            <p className="text-[14px] text-rm-muted mt-[6px]">{body}</p>
          </div>
        ))}
      </div>

      {rentals.length > 0 && (
        <div className="mt-[34px]">
          <h2 className="text-[22px] font-[800] text-rm-ink mb-[12px]">Your rentals</h2>
          <div className="grid md:grid-cols-2 gap-[14px]">
            {rentals.map((r) => (
              <div key={r.id} className="border border-rm-line rounded-[16px] p-[16px] bg-white">
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <h4 className="font-[700] text-rm-ink">{r.equipment_name}</h4>
                    <p className="text-[13px] text-rm-muted">
                      {String(r.start_date).slice(0, 10)} → {String(r.due_date).slice(0, 10)}
                    </p>
                  </div>
                  <span className={`text-[12px] font-[700] px-[10px] py-[4px] rounded-[8px] ${
                    r.status === "returned" ? "bg-rm-softbg text-rm-muted" : "bg-rm-gradient text-white"
                  }`}>{r.status}</span>
                </div>
                {Number(r.overdue_days) > 0 && (
                  <p className="text-[13px] text-red-600 mt-[8px]">
                    {r.overdue_days} day(s) overdue · late fee ₹{Number(r.overdue_fee || 0).toFixed(2)}
                  </p>
                )}
                {r.status === "returned" && (
                  <p className="text-[13px] text-rm-muted mt-[8px]">
                    Deposit refunded ₹{Number(r.deposit_refund != null ? r.deposit_refund : r.deposit_paid || 0).toFixed(2)}
                    {Number(r.damage_fee) > 0 ? ` · damage ₹${Number(r.damage_fee).toFixed(2)}` : ""}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="text-[22px] font-[800] text-rm-ink mt-[34px] mb-[12px]">Available equipment</h2>
      {loading ? (
        <p className="text-rm-muted py-[40px]">Loading equipment…</p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-[18px]">
          {items.map((e) => (
            <div key={e.id} className="border border-rm-line rounded-[18px] p-[20px] bg-white shadow-rm-soft">
              <h3 className="text-[19px] font-[800] text-rm-ink">{e.name}</h3>
              {e.description && <p className="text-[14px] text-rm-muted mt-[6px] line-clamp-3">{e.description}</p>}
              <div className="flex items-end justify-between mt-[14px]">
                <div>
                  <p className="text-[22px] font-[800] text-rm-ink">₹{e.daily_rate}<span className="text-[13px] font-[500] text-rm-muted"> / day</span></p>
                  <p className="text-[13px] text-rm-muted">₹{e.deposit_amount} refundable deposit</p>
                </div>
                <span className={`text-[12px] font-[700] px-[10px] py-[4px] rounded-[8px] ${
                  Number(e.stock_qty) > 0 ? "bg-rm-softbg text-rm-ink" : "bg-rm-softbg text-rm-muted"
                }`}>
                  {Number(e.stock_qty) > 0 ? `${e.stock_qty} available` : "Out of stock"}
                </span>
              </div>
              <p className="text-[12px] text-rm-muted mt-[10px]">{e.late_fee_note}</p>
              <button
                type="button"
                disabled={Number(e.stock_qty) <= 0}
                onClick={() => openRent(e)}
                className={`w-full mt-[14px] py-[10px] rounded-[12px] font-[700] text-[15px] ${
                  Number(e.stock_qty) > 0 ? "bg-rm-gradient text-white" : "bg-rm-softbg text-rm-muted cursor-not-allowed"
                }`}
              >
                {Number(e.stock_qty) > 0 ? "Rent this" : "Unavailable"}
              </button>
            </div>
          ))}
        </div>
      )}

      {!loading && !items.length && (
        <p className="text-rm-muted py-[40px]">No equipment is listed right now. Please check back soon.</p>
      )}

      {active && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setActive(null)}>
          <div className="bg-white rounded-[20px] p-[24px] w-full max-w-[440px]" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[20px] font-[800] text-rm-ink">Rent {active.name}</h3>
            <div className="grid grid-cols-2 gap-[12px] mt-[16px]">
              <div>
                <label className="text-[13px] text-rm-muted">Start date</label>
                <input type="date" className="w-full border border-rm-line rounded-[10px] px-[12px] py-[9px] mt-[4px]"
                       min={today()} value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div>
                <label className="text-[13px] text-rm-muted">Return by</label>
                <input type="date" className="w-full border border-rm-line rounded-[10px] px-[12px] py-[9px] mt-[4px]"
                       min={start} value={due} onChange={(e) => setDue(e.target.value)} />
              </div>
            </div>

            <div className="border border-rm-line rounded-[14px] p-[14px] mt-[16px] bg-rm-softbg text-[14px]">
              <div className="flex justify-between"><span className="text-rm-muted">{days} day(s) × ₹{active.daily_rate}</span><span className="font-[700]">₹{estimate.toFixed(2)}</span></div>
              <div className="flex justify-between mt-[6px]"><span className="text-rm-muted">Refundable deposit</span><span className="font-[700]">₹{Number(active.deposit_amount || 0).toFixed(2)}</span></div>
              <div className="flex justify-between mt-[8px] pt-[8px] border-t border-rm-line">
                <span className="font-[700]">Payable now</span>
                <span className="font-[800]">₹{(estimate + Number(active.deposit_amount || 0)).toFixed(2)}</span>
              </div>
            </div>

            <p className="text-[12px] text-rm-muted mt-[10px]">{active.late_fee_note}</p>

            {!onlineFlow ? (
              <div className="flex gap-[10px] mt-[18px]">
                <button type="button" className="inline-block border border-rm-line text-rm-ink font-[700] text-[14px] px-[16px] py-[10px] rounded-[12px] bg-white hover:border-rm-primary hover:text-rm-primary transition flex-1" onClick={() => setActive(null)}>Cancel</button>
                <button type="button" className="bg-rm-gradient text-white font-[700] rounded-[12px] py-[10px] flex-1" onClick={confirmRent}>
                  Continue to payment
                </button>
              </div>
            ) : (
              <div className="mt-[14px]">
                <V1CashfreeCheckout
                  amount={payable.toFixed(2)}
                  createCheckout={createOnlineCheckout}
                  confirmPayment={confirmOnline}
                  onSuccess={onlineSuccess}
                  onCancel={() => setOnlineFlow(false)}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
